import { acceptHMRUpdate, defineStore } from 'pinia';
import type { SelectOptions } from '@hypercodestudio/basler-components/dist/components/modules/ProductVariantPickerSelect.vue';
import { type ProductInterface } from '~/lib/Shop/generated/schema';
import { isDefined } from '~/utils/guards/isDefined';
import { formatMoney } from '~/utils/shop/formatMoney';
import { createEmptyOption } from '~/stores/productDetailPageStore/helper/createEmptySelectOption';
import { type DecoratedProduct } from '~/utils/shop/DecoratedProduct';
import { encodeBase64 } from '~/utils/encodeBase64';
import { unique } from '~/utils/unique';

export type CrossSellingProductsStoreState = {
  displayedProduct: DecoratedProduct | null;
  selectedCrossSellingProducts: Record<string, string>;
  crossSellingProductsMap: Record<
    string,
    Pick<ProductInterface, 'sku' | 'price_range'>
  >;
  dropDownIds: string[];
};

const EMPTY_ARRAY = Object.freeze([]);

export interface CrossSellingProductsMapInterface {
  [key: string]: Pick<
    ProductInterface,
    | 'sku'
    | 'price_range'
    | 'name'
    | 'attribute_set_name'
    | 'stock_status'
    | 'related_products'
  >;
}

function getGroupId(p: Record<string, any>) {
  return encodeBase64(
    JSON.stringify(encodeURI(p.label + p.description + p.dependency))
  );
}

/**
 * This store is responsible for logic related to product information.
 */
export const useCrossSellingProductsStore = defineStore(
  'cross-selling-products-store',
  () => {
    const displayedProduct = ref<DecoratedProduct | null>(null);
    const compatibleSkus = ref<string[]>([]);
    const skuToGroupIdMap = ref<Record<string, string>>({});
    const groupIdToDependencyMap = ref<Record<string, boolean>>({});
    const selectedCrossSellingProducts = ref<Record<string, string>>({});
    const crossSellingProductsMap = ref<CrossSellingProductsMapInterface>({});

    const { $locale, $textDictionary } = useNuxtApp();

    const _state = {
      displayedProduct,
      selectedCrossSellingProducts,
      crossSellingProductsMap,
      compatibleSkus,
      groupIdToDependencyMap,
      skuToGroupIdMap
    };

    function setDisplayedDecoratedProduct(product: DecoratedProduct | null) {
      displayedProduct.value = product;
      selectedCrossSellingProducts.value = {};

      const crossSellingProducts =
        displayedProduct.value?.product.custom_related_products?.filter(
          isDefined
        ) ?? EMPTY_ARRAY;

      crossSellingProductsMap.value = crossSellingProducts
        .map((e) => {
          return e.products;
        })
        .flat()
        .filter(isDefined)
        .reduce((reducer, p) => {
          return {
            ...reducer,
            [p.sku! as string]: p
          };
        }, {});

      compatibleSkus.value = Object.keys(crossSellingProductsMap.value);

      groupIdToDependencyMap.value = crossSellingProducts.reduce(
        (reducer, p) => {
          return {
            ...reducer,
            [getGroupId(p)]: p.dependency ?? false
          };
        },
        {} as Record<string, boolean>
      );

      skuToGroupIdMap.value = crossSellingProducts.reduce((reducer, p) => {
        const groupId = getGroupId(p);
        return {
          ...reducer,
          ...(p.products?.filter(isDefined).reduce((innerReducer, product) => {
            return {
              ...innerReducer,
              [product.sku!]: groupId
            };
          }, {}) ?? {})
        };
      }, {} as Record<string, string>);
    }

    /**
     * A transformed list of items used for the according ui component.
     */
    const items = computed(() => {
      const crossSellingProducts =
        displayedProduct.value?.product.custom_related_products
          ?.filter(isDefined)
          .filter((crp) => {
            return (crp?.products?.length || 0) > 0;
          }) ?? EMPTY_ARRAY;
      if (
        !isDefined(crossSellingProducts) ||
        !Array.isArray(crossSellingProducts) ||
        crossSellingProducts.length <= 0
      ) {
        return EMPTY_ARRAY;
      }
      return crossSellingProducts.filter(isDefined).map((entry) => {
        const products = entry.products?.filter(isDefined);
        const id = getGroupId(entry);
        const selectOptions: SelectOptions[] =
          products
            ?.filter(
              (product) =>
                !entry.dependency || compatibleSkus.value.includes(product.sku!)
            )
            .map((product) => {
              return {
                label: `${product.name ?? ''} +${formatMoney(
                  product?.price_range?.minimum_price?.final_price,
                  $locale.value
                )}`,
                labelInfo: '', // keep empty string, otherwise '-' will be rendered infront of the price
                value: product.sku!,
                selected: selectedCrossSellingSkus.value.includes(product.sku!)
              } as SelectOptions;
            }) ?? [];

        selectOptions?.unshift(
          createEmptyOption(
            entry.select_label ??
              $textDictionary.value[
                'pdp.crossselling-addons.default-option.label'
              ] ??
              'Please select an option',
            selectedCrossSellingProducts.value[id] == null
          )
        );

        return {
          id,
          label: entry.label ?? '',
          labelTooltip: entry.explanation
            ? {
                tag: 'span',
                tooltipText: entry.explanation,
                tooltipPlacement: 'right'
              }
            : undefined,
          isActive: false,
          isDisabled: selectOptions.length <= 1,
          selectOptions
        };
      });
    });

    /**
     * The price (sum) of all selected options. This does NOT include
     * the first required option (which is usually the base product).
     * Therefore, this function returns only the sum of selected addons.
     *
     * XXX: calculating prices should be done by shop.
     */
    const selectedCrossSellingProductsPrice = computed<number>(() => {
      return selectedCrossSellingSkus.value
        .map(
          (sku) =>
            crossSellingProductsMap.value[sku]?.price_range?.minimum_price
              ?.final_price?.value ?? 0
        )
        .reduce((collector, price) => collector + price, 0);
    });

    /**
     * A list of skus for each selected option.
     *
     * XXX: this does not include the first required option
     */
    const selectedCrossSellingSkus = computed<ReadonlyArray<string>>(
      () => Object.values(selectedCrossSellingProducts.value) ?? []
    );

    function selectCrossSellingProduct(groupId: string, productSku: string) {
      if (productSku === '') {
        delete selectedCrossSellingProducts.value[groupId];
      } else {
        selectedCrossSellingProducts.value[groupId] = productSku;
      }

      if (groupIdToDependencyMap.value[groupId]) {
        if (selectedCrossSellingSkus.value.length === 0) {
          // if no selection, use all skus
          compatibleSkus.value = Object.keys(crossSellingProductsMap.value);
        } else {
          compatibleSkus.value = unique(
            selectedCrossSellingSkus.value
              .map((sku) => {
                const productsGroupId = skuToGroupIdMap.value[sku];
                if (groupIdToDependencyMap.value[productsGroupId]) {
                  return [
                    ...(crossSellingProductsMap.value[sku].related_products
                      ?.map((p) => p?.sku)
                      .filter(isDefined) ?? []),
                    ...(selectedCrossSellingSkus.value.length === 1
                      ? Object.values(crossSellingProductsMap.value)
                          ?.filter(
                            (e) =>
                              skuToGroupIdMap.value[e.sku!] === productsGroupId
                          )
                          .map((e) => e.sku)
                          .filter(isDefined) ?? []
                      : [sku])
                  ];
                } else {
                  return [sku];
                }
              })
              .flat()
          );
        }
      }
    }

    return {
      _state,
      items,
      selectedCrossSellingProductsPrice,
      selectedCrossSellingSkus,
      selectCrossSellingProduct,
      setDisplayedDecoratedProduct
    };
  }
);

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useCrossSellingProductsStore, import.meta.hot)
  );
}
